import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import FitText from '../../../shared/components/FitText';
import {
  ASSESSMENT_COMPONENTS,
  ASSESSMENT_TABS,
  ATTENDANCE_TAB,
  GRADES_SUMMARY_TAB,
} from '../../../shared/constant/assessmentTabs';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { useOwnSubject } from '../hooks/useOwnSubject';
import { useSubjectRoster } from '../hooks/useSubjectRoster';
import AttendancePanel from './AttendancePanel';
import AssessmentPanel from './AssessmentPanel';
import GradesSummaryPanel from './GradesSummaryPanel';

const TABS = [...ASSESSMENT_TABS, GRADES_SUMMARY_TAB];

function GradesPerSubjectPage() {
  const { subjectId } = useParams();
  // Links from elsewhere (e.g. the dashboard's attention list) can pick the tab, term and section to open on.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : TABS[0]
  );
  const { subject, loading, error } = useOwnSubject(subjectId);
  const sections = useMemo(
    () => (subject?.subject_sections ?? []).map((s) => s.section).sort(),
    [subject]
  );
  const { roster, loading: rosterLoading, error: rosterError } = useSubjectRoster(subject?.grade_level, sections);

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <main className="flex-1 min-h-0 w-full p-4 lg:w-4/5 lg:mx-auto flex flex-col space-y-4">
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/dashboard/grades" aria-label="Back to grades" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <FitText as="h1" className="min-w-0 flex-1 text-2xl font-semibold text-[#0b4a71]">
            {subject?.name ?? 'Grades'}
          </FitText>
        </div>

        {loading ? (
          <p className="w-full text-center text-gray-500 py-12">Loading…</p>
        ) : error ? (
          <p className="w-full text-center text-red-600 py-12">{error}</p>
        ) : (
          <div className="w-full flex-1 min-h-0 flex flex-col space-y-4">
            <p className="shrink-0 text-md text-gray-500">
              {[subject.grade_level && getGradeLevelLabel(subject.grade_level), subject.category]
                .filter(Boolean)
                .join(' - ')}
            </p>

            <div className="shrink-0 flex gap-2 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                    activeTab === tab
                      ? 'bg-[#4ea6d0] border-[#4ea6d0] text-white'
                      : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {rosterError && (
              <p className="shrink-0 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{rosterError}</p>
            )}

            {activeTab === ATTENDANCE_TAB ? (
              <AttendancePanel
                subject={subject}
                sections={sections}
                roster={roster}
                rosterLoading={rosterLoading}
                initialSection={searchParams.get('section')}
              />
            ) : activeTab === GRADES_SUMMARY_TAB ? (
              <GradesSummaryPanel
                subject={subject}
                sections={sections}
                roster={roster}
                rosterLoading={rosterLoading}
                initialTerm={searchParams.get('term')}
                initialSection={searchParams.get('section')}
              />
            ) : (
              <AssessmentPanel
                key={activeTab}
                subject={subject}
                config={ASSESSMENT_COMPONENTS[activeTab]}
                sections={sections}
                roster={roster}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default GradesPerSubjectPage;
